import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  FormLabel,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  AppDatePicker,
  AppInput,
  AppSelect,
  MasterAddressFields,
  OutlineButton,
  PhotoUploadField,
  PrimaryButton,
} from '../../../../components/ui';
import { sitesForBu } from '../../../../data/master/businessUnits';
import {
  CONSTITUTION_OPTIONS,
  LOCAL_PROFESSIONAL_OPTIONS,
  MAJOR_CATEGORY_OPTIONS,
} from '../../../../data/master/vendors';
import {
  createVendor,
  updateVendor,
} from '../../../../repositories/vendor.repository';
import { lookupVillages, lookupBusinessUnits, lookupSites } from '../../../../repositories/lookup.repository';
import { dbIdForCode } from '../../../../utils/entityResolve';
import { composeFullName } from '../../../../utils/personName';
import {
  formatAadhaarInput,
  validateVendorForm,
  VENDOR_FIELD_LIMITS,
} from '../../../../utils/vendorValidation';
import { MdSwapHoriz } from 'react-icons/md';

const emptyForm = {
  gstNumber: '',
  tradeAgencyName: '',
  constitution: 'Proprietorship',
  dateOfIncorporation: '',
  ownerName: '',
  fatherHusbandName: '',
  surname: '',
  mobile: '',
  email: '',
  address1: '',
  address2: '',
  villageId: '',
  pin: '',
  buId: '',
  siteId: '',
  kronosNumber: '',
  localProfessional: '',
  majorCategory: '',
  photo: null,
  aadhaarNumber: '',
  pan: '',
  gstCertificate: null,
  businessOfficePhoto: null,
  aadhaarFront: null,
  aadhaarBack: null,
  panCard: null,
  verificationStatus: 'Pending',
  rejectionReason: '',
  active: true,
};

function vendorToForm(v) {
  if (!v) return emptyForm;
  return {
    gstNumber: v.gstNumber || '',
    tradeAgencyName: v.tradeAgencyName || '',
    constitution: v.constitution || 'Proprietorship',
    dateOfIncorporation: v.dateOfIncorporation || '',
    ownerName: v.ownerName || '',
    fatherHusbandName: v.fatherHusbandName || '',
    surname: v.surname || '',
    mobile: v.mobile || '',
    email: v.email || '',
    address1: v.address1 || '',
    address2: v.address2 || '',
    villageId: v.villageId || '',
    pin: v.pin || '',
    buId: v.buId || '',
    siteId: v.siteId || '',
    kronosNumber: v.kronosNumber || '',
    localProfessional: v.localProfessional || '',
    majorCategory: v.majorCategory || '',
    photo: v.photo || null,
    aadhaarNumber: v.aadhaarNumber || '',
    pan: v.pan || '',
    gstCertificate: v.gstCertificate || null,
    businessOfficePhoto: v.businessOfficePhoto || null,
    aadhaarFront: v.aadhaarFront || null,
    aadhaarBack: v.aadhaarBack || null,
    panCard: v.panCard || null,
    verificationStatus: v.verificationStatus || 'Pending',
    rejectionReason: v.rejectionReason || '',
    active: v.active !== false,
  };
}

export function VendorFormModal({
  isOpen,
  onClose,
  vendor = null,
  onSaved,
  villages: villagesProp,
  businessUnits: buProp,
  sites: sitesProp,
}) {
  const toast = useToast();
  const editingNumericId = vendor?.id || null;
  const displayCode = vendor?.vendorId || null;
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [villages, setVillages] = useState(villagesProp || []);
  const [businessUnits, setBusinessUnits] = useState(buProp || []);
  const [sites, setSites] = useState(sitesProp || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setForm(vendorToForm(vendor));
    setErrors({});
    let cancelled = false;
    (async () => {
      try {
        const [vItems, buItems, siteItems] = await Promise.all([
          villagesProp?.length
            ? Promise.resolve(villagesProp)
            : lookupVillages(),
          buProp?.length
            ? Promise.resolve(buProp)
            : lookupBusinessUnits(),
          sitesProp?.length
            ? Promise.resolve(sitesProp)
            : lookupSites(),
        ]);
        if (cancelled) return;
        setVillages(vItems || []);
        setBusinessUnits(buItems || []);
        setSites(siteItems || []);
      } catch (err) {
        if (!cancelled) {
          toast({
            title: err?.message || 'Failed to load form options',
            status: 'error',
            position: 'top-right',
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, vendor, villagesProp, buProp, sitesProp, toast]);

  const ownerFullName = composeFullName(
    form.ownerName,
    form.fatherHusbandName,
    form.surname,
  );

  const buOptions = useMemo(
    () =>
      businessUnits
        .filter((b) => b.active !== false)
        .map((b) => ({
          value: b.buId,
          label: b.shortName,
          description: b.fullNameGst || b.buId,
        })),
    [businessUnits],
  );

  const sitesForSelectedBu = useMemo(
    () => sitesForBu(sites, form.buId),
    [sites, form.buId],
  );

  const siteOptions = useMemo(
    () =>
      sitesForSelectedBu.map((s) => ({
        value: s.siteId,
        label: s.siteName,
        description: s.projectOperation || s.siteId,
      })),
    [sitesForSelectedBu],
  );

  const showSiteField = Boolean(form.buId && sitesForSelectedBu.length > 0);

  const patchForm = (partial) => {
    setForm((f) => ({ ...f, ...partial }));
    const keys = Object.keys(partial);
    if (keys.length) {
      setErrors((prev) => {
        const next = { ...prev };
        keys.forEach((k) => delete next[k]);
        return next;
      });
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const nextErrors = validateVendorForm(form, { requireSite: showSiteField });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      toast({
        title: 'Fix the highlighted fields',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    const composed = composeFullName(
      form.ownerName,
      form.fatherHusbandName,
      form.surname,
    );
    const villageDbId =
      vendor?.villageDbId && vendor?.villageId === form.villageId
        ? vendor.villageDbId
        : dbIdForCode(villages, form.villageId, { codeKey: 'villageId' });
    const buDbId =
      vendor?.buDbId && vendor?.buId === form.buId
        ? vendor.buDbId
        : dbIdForCode(businessUnits, form.buId, { codeKey: 'buId' });
    const siteDbId =
      vendor?.siteDbId && vendor?.siteId === form.siteId
        ? vendor.siteDbId
        : dbIdForCode(sites, form.siteId, { codeKey: 'siteId' });

    const payload = {
      gstNumber: form.gstNumber.trim().toUpperCase(),
      tradeAgencyName: form.tradeAgencyName.trim(),
      constitution: form.constitution,
      dateOfIncorporation: form.dateOfIncorporation,
      ownerName: form.ownerName.trim(),
      fatherHusbandName: form.fatherHusbandName.trim(),
      surname: form.surname.trim(),
      ownerFullName: composed,
      mobile: form.mobile.trim(),
      email: form.email.trim(),
      address1: form.address1.trim(),
      address2: form.address2.trim(),
      villageId: form.villageId,
      villageDbId,
      pin: form.pin.trim(),
      buId: form.buId,
      buDbId,
      siteId: form.siteId,
      siteDbId,
      kronosNumber: form.kronosNumber.trim(),
      localProfessional: form.localProfessional || '',
      majorCategory: form.majorCategory || '',
      photo: form.photo,
      aadhaarNumber: form.aadhaarNumber.trim(),
      pan: form.pan.trim().toUpperCase(),
      gstCertificate: form.gstCertificate,
      businessOfficePhoto: form.businessOfficePhoto,
      aadhaarFront: form.aadhaarFront,
      aadhaarBack: form.aadhaarBack,
      panCard: form.panCard,
      verificationStatus: editingNumericId
        ? form.verificationStatus || 'Pending'
        : 'Pending',
      rejectionReason: editingNumericId ? form.rejectionReason || '' : '',
      active: editingNumericId
        ? form.verificationStatus !== 'Rejected' &&
          form.verificationStatus !== 'Blocked' &&
          form.active
        : true,
    };

    setSaving(true);
    try {
      if (editingNumericId) {
        await updateVendor(editingNumericId, payload);
        toast({
          title: 'Vendor updated',
          description: `${payload.tradeAgencyName} · ${displayCode || editingNumericId}`,
          status: 'success',
          position: 'top-right',
        });
      } else {
        const created = await createVendor(payload);
        toast({
          title: 'Vendor saved',
          description: `${created.tradeAgencyName} · ${created.vendorId} · Pending verification`,
          status: 'success',
          position: 'top-right',
        });
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save vendor',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="4xl"
      isCentered
      scrollBehavior="inside"
      blockScrollOnMount
      trapFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        as="form"
        onSubmit={handleSave}
        bg="bg.surface"
        borderRadius="md"
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0}>
          {displayCode ? `Edit vendor · ${displayCode}` : 'Add vendor (Vendor Master)'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
          <VStack align="stretch" spacing={5}>
            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Business Unit & Site
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <AppSelect
                  label="Business Unit"
                  isRequired
                  isSearchable
                  searchPlaceholder="Search BU…"
                  placeholder="Select business unit"
                  value={form.buId}
                  onChange={(buId) =>
                    patchForm({
                      buId: buId || '',
                      siteId: '',
                    })
                  }
                  options={buOptions}
                  isInvalid={!!errors.buId}
                  errorMessage={errors.buId}
                />
                {showSiteField ? (
                  <AppSelect
                    label="Site"
                    isRequired
                    isSearchable
                    searchPlaceholder="Search site…"
                    placeholder="Select site"
                    value={form.siteId}
                    onChange={(siteId) => patchForm({ siteId: siteId || '' })}
                    options={siteOptions}
                    isInvalid={!!errors.siteId}
                    errorMessage={errors.siteId}
                  />
                ) : null}
              </SimpleGrid>
            </Box>

            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Business details
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                <AppInput
                  label="Trade / Agency Name"
                  isRequired
                  value={form.tradeAgencyName}
                  onChange={(e) => patchForm({ tradeAgencyName: e.target.value })}
                  maxLength={VENDOR_FIELD_LIMITS.tradeAgencyName.max}
                  isInvalid={!!errors.tradeAgencyName}
                  errorMessage={errors.tradeAgencyName}
                />
                <AppInput
                  label="Adani Kronos Number (gate pass)"
                  value={form.kronosNumber}
                  onChange={(e) => patchForm({ kronosNumber: e.target.value })}
                  placeholder="e.g. AK-88421"
                  maxLength={VENDOR_FIELD_LIMITS.kronosNumber.max}
                  isInvalid={!!errors.kronosNumber}
                  errorMessage={errors.kronosNumber}
                />
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <AppInput
                  label="GST Number"
                  value={form.gstNumber}
                  onChange={(e) =>
                    patchForm({ gstNumber: e.target.value.toUpperCase().slice(0, 15) })
                  }
                  placeholder="15-character GSTIN"
                  maxLength={15}
                  isInvalid={!!errors.gstNumber}
                  errorMessage={errors.gstNumber}
                />
                <AppSelect
                  label="Constitution of Business"
                  isRequired
                  value={form.constitution}
                  onChange={(v) => patchForm({ constitution: v })}
                  options={CONSTITUTION_OPTIONS.map((c) => ({ value: c, label: c }))}
                  isInvalid={!!errors.constitution}
                  errorMessage={errors.constitution}
                />
                <AppDatePicker
                  label="Date of Incorporation"
                  value={form.dateOfIncorporation}
                  onChange={(e) => patchForm({ dateOfIncorporation: e.target.value })}
                  max={new Date().toISOString().slice(0, 10)}
                  isInvalid={!!errors.dateOfIncorporation}
                  errorMessage={errors.dateOfIncorporation}
                  helperText="Optional · type DD/MM/YYYY (/ added automatically)"
                />
                <AppSelect
                  label="LOCAL / PROFESSIONAL"
                  value={form.localProfessional}
                  onChange={(v) => patchForm({ localProfessional: v || '' })}
                  options={[
                    { value: '', label: 'Select…' },
                    ...LOCAL_PROFESSIONAL_OPTIONS,
                  ]}
                />
                <AppSelect
                  label="Major Category"
                  value={form.majorCategory}
                  onChange={(v) => patchForm({ majorCategory: v || '' })}
                  options={[
                    { value: '', label: 'Select…' },
                    ...MAJOR_CATEGORY_OPTIONS,
                  ]}
                />
              </SimpleGrid>
            </Box>

            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Owner & contact
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={4}>
                <AppInput
                  label="Owner / Partner Name"
                  isRequired
                  value={form.ownerName}
                  onChange={(e) => patchForm({ ownerName: e.target.value })}
                  maxLength={VENDOR_FIELD_LIMITS.ownerName.max}
                  isInvalid={!!errors.ownerName}
                  errorMessage={errors.ownerName}
                />
                <AppInput
                  label="Father’s / Husband’s Name"
                  value={form.fatherHusbandName}
                  onChange={(e) => patchForm({ fatherHusbandName: e.target.value })}
                  maxLength={VENDOR_FIELD_LIMITS.fatherHusbandName.max}
                />
                <AppInput
                  label="Surname"
                  value={form.surname}
                  onChange={(e) => patchForm({ surname: e.target.value })}
                  maxLength={VENDOR_FIELD_LIMITS.surname.max}
                  isInvalid={!!errors.surname}
                  errorMessage={errors.surname}
                />
                <AppInput
                  label="Owner / Partner Full Name"
                  value={ownerFullName}
                  isReadOnly
                  placeholder="From name parts"
                />
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                <AppInput
                  label="Mobile Number"
                  isRequired
                  leftAddon="+91"
                  value={form.mobile}
                  onChange={(e) =>
                    patchForm({ mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })
                  }
                  placeholder="10-digit mobile"
                  maxLength={10}
                  inputMode="numeric"
                  isInvalid={!!errors.mobile}
                  errorMessage={errors.mobile}
                />
                <AppInput
                  label="Email"
                  type="email"
                  isRequired
                  value={form.email}
                  onChange={(e) => patchForm({ email: e.target.value })}
                  maxLength={VENDOR_FIELD_LIMITS.email.max}
                  isInvalid={!!errors.email}
                  errorMessage={errors.email}
                />
              </SimpleGrid>
              <PhotoUploadField
                label="Profile / Logo"
                value={form.photo}
                onChange={(photo) => patchForm({ photo })}
              />
            </Box>

            <Box>
              <Heading size="xs" mb={3} color="text.secondary">
                Address
              </Heading>
              <MasterAddressFields
                villages={villages}
                address1={form.address1}
                address2={form.address2}
                villageId={form.villageId}
                pin={form.pin}
                onChange={patchForm}
                errors={errors}
              />
            </Box>

            <Box>
              <Heading size="xs" mb={1} color="text.secondary">
                KYC documents
              </Heading>
              <Text fontSize="13px" color="text.secondary" mb={3}>
                GST & business photos, owner Aadhaar (required intent), PAN optional — same as
                mobile signup.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                <PhotoUploadField
                  label="GST certificate"
                  value={form.gstCertificate}
                  onChange={(gstCertificate) => patchForm({ gstCertificate })}
                  helperText="Upload GST certificate image."
                />
                <PhotoUploadField
                  label="Business / office photo"
                  value={form.businessOfficePhoto}
                  onChange={(businessOfficePhoto) => patchForm({ businessOfficePhoto })}
                  helperText="Office / agency premises photo."
                />
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                <AppInput
                  label="Aadhaar number"
                  value={form.aadhaarNumber}
                  onChange={(e) =>
                    patchForm({ aadhaarNumber: formatAadhaarInput(e.target.value) })
                  }
                  placeholder="XXXX-XXXX-XXXX"
                  inputMode="numeric"
                  maxLength={14}
                  isInvalid={!!errors.aadhaarNumber}
                  errorMessage={errors.aadhaarNumber}
                />
                <AppInput
                  label="Owner PAN (optional)"
                  value={form.pan}
                  onChange={(e) =>
                    patchForm({ pan: e.target.value.toUpperCase().slice(0, 10) })
                  }
                  placeholder="ABCDE1234F"
                  maxLength={10}
                  isInvalid={!!errors.pan}
                  errorMessage={errors.pan}
                />
              </SimpleGrid>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                <PhotoUploadField
                  label="Aadhaar — Front"
                  value={form.aadhaarFront}
                  onChange={(aadhaarFront) => patchForm({ aadhaarFront })}
                />
                <PhotoUploadField
                  label="Aadhaar — Back"
                  value={form.aadhaarBack}
                  onChange={(aadhaarBack) => patchForm({ aadhaarBack })}
                />
                <PhotoUploadField
                  label="PAN card (optional)"
                  value={form.panCard}
                  onChange={(panCard) => patchForm({ panCard })}
                />
              </SimpleGrid>
              {(form.aadhaarFront || form.aadhaarBack) && (
                <OutlineButton
                  mt={3}
                  size="sm"
                  leftIcon={<MdSwapHoriz />}
                  onClick={() =>
                    patchForm({
                      aadhaarFront: form.aadhaarBack,
                      aadhaarBack: form.aadhaarFront,
                    })
                  }
                  isDisabled={!form.aadhaarFront || !form.aadhaarBack}
                >
                  Swap Aadhaar front / back
                </OutlineButton>
              )}
            </Box>

            <HStack justify="space-between">
              <FormLabel mb={0}>Active</FormLabel>
              <Switch
                isChecked={form.active}
                onChange={(e) => patchForm({ active: e.target.checked })}
                colorScheme="yellow"
              />
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter
          flexShrink={0}
          gap={2}
          borderTopWidth="1px"
          borderColor="border.subtle"
          bg="bg.surface"
          zIndex={2}
        >
          <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
            Cancel
          </OutlineButton>
          <PrimaryButton type="submit" isLoading={saving}>
            Save
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
